import React from "react";
import { ColumnVisibilityState } from "features/MappingEditor/types";
import {
  replaceVisibilitySettings,
  saveColumnState,
} from "./components/columnVisibility";
import {
  dynamicTableStore,
  TableMode,
} from "features/DynamicTable/DynamicTableStore";
import { PropertyBrowserTreeNode } from "api-client";
import { DynamicColumnOUT } from "api-client";
import clsx from "clsx";
import { GroupingRemoveIcon } from "features/DynamicTable/utils";
import ColumnSelectorBrowser from "./features/ColumnSelectorBrowser";
import { useTranslation } from "react-i18next";
import { OBJECT_COUNT_ID } from "./consts";
import { COLUMNS_GROUPS } from "features/MappingEditor/useTableColumns";
import { FilterRule } from "./features/Filters/FilterByPropertyBrowser";
import NumericCell from "./components/cells/NumericCell";
import TextCell from "./components/cells/TextCell";
import ChipCell from "./components/cells/ChipCell";

interface Info {
  row: { original: { [key: string]: string | number | string[] | number[] } };
}

const useDynamicTable = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";

  const buildColumnsVisibilityState = (): ColumnVisibilityState => {
    /**
     * Builds the current state using the available column groups and
     * visible columns
     */
    const previousState: ColumnVisibilityState = Object.keys(
      dynamicTableStore.columnsVisibilityState
    ).reduce((r, c) => Object.assign(r, { [c]: false }), {});

    const visibleColumnKeys = new Set(
      dynamicTableStore.visibleColumnProperties.map((e) => e.column_key)
    );

    const allColumns = dynamicTableStore.dynamicColumns.map((e) => {
      return { [e.key_name]: visibleColumnKeys.has(e.key_name) };
    });

    const reducedList = allColumns.reduce(
      (r, c) => Object.assign(r, c),
      previousState
    );
    return reducedList;
  };

  const updateColumnsVisibilitySavedState = () => {
    /**
     * Updates the available columns and the visible columns in one operation
     */
    const newState = replaceVisibilitySettings();
    saveColumnsVisibilityState(newState);
  };

  const saveColumnsVisibilityState = (newState?: {
    [key: string]: boolean;
  }) => {
    /**
     * Builds the current state of column visibility from the DMEStore
     * and save its in the local storage and in the DMEStore
     */
    let savingState: { [key: string]: boolean } = newState ?? {};
    if (!newState) {
      savingState = buildColumnsVisibilityState();
    }
    dynamicTableStore.setColumnsVisibilityState(savingState);
    saveColumnState(savingState);
  };

  const reorderListOfObjects = (
    listOfObjects: PropertyBrowserTreeNode[],
    sortingOrder: string[]
  ) => {
    listOfObjects.sort((a, b) => {
      const indexA = sortingOrder.indexOf(a.column_key);
      const indexB = sortingOrder.indexOf(b.column_key);

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      } else if (indexA !== -1) {
        return -1;
      } else if (indexB !== -1) {
        return 1;
      } else {
        return 0;
      }
    });
  };

  function getColumnsOrderSavedState(): string[] | null {
    return JSON.parse(
      localStorage.getItem(`${dynamicTableStore.mode}_columns_order`) ?? "null"
    );
  }

  function retrieveOrderSavedState() {
    let orderState = getColumnsOrderSavedState();
    if (!orderState) {
      orderState = defaultColumnOrder();
    }

    reorderListOfObjects(dynamicTableStore.visibleColumnProperties, orderState);
    saveColumnsOrder();
  }

  function defaultColumnOrder(): string[] {
    const staticOrder: string[] = [
      "physical_object.component",
      "physical_object.material",
      "physical_object.area",
      "physical_object.volume",
      "match_calculations.mass",
      "match_calculations.gwp",
    ];
    const columnVisibility = buildColumnsVisibilityState();
    return staticOrder.filter((k) => columnVisibility[k]);
  }

  function saveColumnsOrder() {
    const columnsOrder = dynamicTableStore.visibleColumnProperties.map(
      (item) => item.column_key
    );
    dynamicTableStore.setColumnsOrder(columnsOrder);
    localStorage.setItem(
      `${dynamicTableStore.mode}_columns_order`,
      JSON.stringify(columnsOrder)
    );
  }

  function saveFilterRules() {
    localStorage.setItem(
      `${dynamicTableStore.mode}_filter`,
      JSON.stringify({
        ...dynamicTableStore.filterStore.selectedFilterByItems,
        rules: dynamicTableStore.filterStore.selectedFilterByItems.rules.map(
          (item: FilterRule) => ({
            ...item,
            property: item.property?.column_key,
          })
        ),
      })
    );
  }

  function retrieveFiltersState() {
    const savedState = getFilterRulesSavedState();
    if (savedState) {
      const retrievedFilters = {
        ...savedState,
        rules: savedState.rules
          .map((rule: { property: string }) => ({
            ...rule,
            property: dynamicTableStore.propertiesDictionary[rule.property],
          }))
          .filter(
            (rule: { property: PropertyBrowserTreeNode | undefined }) =>
              rule.property
          ),
      };
      if (retrievedFilters.rules.length) {
        dynamicTableStore.filterStore.setSelectedFilterByItems(
          retrievedFilters
        );
      }
    }
  }

  function getFilterRulesSavedState() {
    return JSON.parse(
      localStorage.getItem(`${dynamicTableStore.mode}_filter`) ?? "null"
    );
  }

  function saveSortRules() {
    localStorage.setItem(
      `${dynamicTableStore.mode}_sort`,
      JSON.stringify(
        dynamicTableStore.filterStore.selectedSortedByItems.map((item) => ({
          ...item,
          property: item.property?.column_key,
        }))
      )
    );
  }

  function getSortRulesSavedState() {
    return JSON.parse(
      localStorage.getItem(`${dynamicTableStore.mode}_sort`) ?? "null"
    );
  }

  function retrieveSortState() {
    const savedState = getSortRulesSavedState();
    if (savedState) {
      const retrievedSorts = savedState
        .map((item: { property: string }) => ({
          ...item,
          property: dynamicTableStore.visibleColumnProperties.find(
            (rec) => rec.column_key === item.property
          ),
        }))
        .filter((rule: { property: PropertyBrowserTreeNode }) => rule.property);

      if (retrievedSorts.length) {
        dynamicTableStore.filterStore.setSelectedSortedByItems(retrievedSorts);
      }
    }
  }

  function saveGroupingRules() {
    localStorage.setItem(
      `${dynamicTableStore.mode}_groupby`,
      JSON.stringify(
        dynamicTableStore.filterStore.selectedGroupByItems.map(
          (item: PropertyBrowserTreeNode) => item?.column_key
        )
      )
    );
  }

  function getGroupingRulesSavedState() {
    return JSON.parse(
      localStorage.getItem(`${dynamicTableStore.mode}_groupby`) ?? "null"
    );
  }

  function retrieveGroupingState() {
    /*
     * Returns only the properties exist
     */
    const savedState = getGroupingRulesSavedState();
    if (savedState) {
      const retrievedFilters = savedState
        .map((key_name: string) => {
          return dynamicTableStore.propertiesDictionary[key_name];
        })
        .filter(Boolean);

      if (retrievedFilters.length) {
        dynamicTableStore.filterStore.setSelectedGroupByItems(retrievedFilters);
      }
    }
  }

  function clearGrouping() {
    dynamicTableStore.filterStore.removeAllGroupingRules();
    dynamicTableStore.refreshTableObjects();
  }

  async function refreshTable() {
    await dynamicTableStore.refreshTableObjects();
  }

  const isME = dynamicTableStore.mode === TableMode.MappingEditor;
  const isInventory = dynamicTableStore.mode === TableMode.Inventory;
  const MAPPING_EDITOR_STICKY_COULUMN_WIDTH = 340;

  const headerCell = (
    group_name: string,
    class_names?: string,
    isGroupingColumns?: boolean,
    isLastGroup?: boolean
  ) => (
    <div
      className={clsx(
        "px-2 py-1 flex justify-between w-full text-gray-500 items-center",
        "whitespace-nowrap text-xs",
        class_names
      )}
    >
      <span
        className={clsx(
          "z-1",
          isGroupingColumns ? "bg-gray-500" : "bg-gray-50"
        )}
      >
        {group_name}
      </span>
      <div
        className={clsx("flex items-center sticky pr-1 z-2", {
          "right-0 pl-1": isInventory,
          "bg-gray-50": !isGroupingColumns && isInventory,
        })}
        style={
          isME ? { right: MAPPING_EDITOR_STICKY_COULUMN_WIDTH } : undefined
        }
      >
        {(isLastGroup || isME || isGroupingColumns) && (
          <ColumnSelectorBrowser
            groupName={
              isGroupingColumns
                ? COLUMNS_GROUPS.groupingColumns
                : COLUMNS_GROUPS.infoColumns
            }
          />
        )}

        {isGroupingColumns && (
          <GroupingRemoveIcon
            size="16"
            stroke="3"
            classNames="ml-2 cursor-pointer"
            onClick={clearGrouping}
          />
        )}
      </div>
    </div>
  );

  const columnCell = (
    isGroupingColumn: boolean,
    col: DynamicColumnOUT,
    extraClasses?: string,
    maskDuplicates?: boolean
  ) => ({
    accessorFn: (row: { [key: string]: string }) => row[col.key_name],
    id: col.key_name,
    label: isEng
      ? col.short_with_unit || col.friendly_name
      : col.short_with_unit_de || col.friendly_name_de,
    header: () => (
      <div className="flex flex-col">
        <span className="truncate">
          {isEng
            ? col.short_with_unit || col.friendly_name
            : col.short_with_unit_de || col.friendly_name_de}
        </span>
        {dynamicTableStore.filterStore.hasGrouping() && !isGroupingColumn ? (
          <span className="text-xs text-gray-500 font-medium">
            {col.type === "NUMERIC"
              ? t("mappingEditor.sum")
              : t("mappingEditor.uniqueness")}
          </span>
        ) : null}
      </div>
    ),
    cell: (info: Info) => {
      // Data prep
      let value: string | number | string[] | number[] =
        info.row.original[col.key_name];
      if (Array.isArray(value)) {
        value = value.filter((v) => v !== null) as string[] | number[];
        if (value.length === 0) {
          value = "";
        } else if (value.length === 1) {
          value = value[0];
        }
      }

      // Dispatch the correct cell depending on column type
      if (Array.isArray(value)) {
        return <ChipCell value={value} />;
      } else if (col.type === "NUMERIC") {
        const numericValue =
          value !== null && value !== undefined && value !== ""
            ? (value as number)
            : null;
        const calculation_count = info.row.original[
          "calculation_count"
        ] as number;
        const object_count = info.row.original[OBJECT_COUNT_ID] as number;
        const shouldBeMasked =
          maskDuplicates && object_count === 1 && calculation_count !== 1;
        const extraCSS = shouldBeMasked ? "text-gray-400" : "";

        return <NumericCell value={numericValue} extraCssClasses={extraCSS} />;
      } else if (col.type === "MULTI_SELECT") {
        return <ChipCell value={value} />;
      } else {
        const count = info.row.original[`${col.key_name}__count`] as number;
        return <TextCell value={value as string} count={count} />;
      }
    },
    enableColumnFilter: false,
    enableSorting: false,
    enableMenu: true,
    meta: {
      skipTableTooltip: col.type === "NUMERIC" || col.type === "MULTI_SELECT",
    },
    cellClassName: clsx(
      {
        "bg-gray-50 !border-r-gray-100": isGroupingColumn,
      },
      extraClasses
    ),
    headerClassName: clsx(
      {
        "bg-gray-100": isGroupingColumn,
        "border-r-0":
          col.key_name === dynamicTableStore.columnsOrder.slice(-1)[0],
      },
      extraClasses
    ),
  });

  const objectCountCell = (extraClasses?: string) => ({
    id: OBJECT_COUNT_ID,
    header: () => (
      <span className="truncate">{t("mappingEditor.numberOfObjects")}</span>
    ),
    cell: (info: Info) => (
      <TextCell value={info.row.original[OBJECT_COUNT_ID]} />
    ),
    enableColumnFilter: false,
    cellClassName: clsx("bg-gray-50 !border-r-gray-100", extraClasses),
    headerClassName: clsx("bg-gray-100", extraClasses),
    size: 70,
    maxWidth: 70,
    minWidth: 70,
    maxSize: 70,
  });

  return {
    buildColumnsVisibilityState,
    updateColumnsVisibilitySavedState,
    saveColumnsVisibilityState,
    getColumnsOrderSavedState,
    retrieveOrderSavedState,
    saveColumnsOrder,
    saveFilterRules,
    saveSortRules,
    retrieveFiltersState,
    retrieveSortState,
    saveGroupingRules,
    retrieveGroupingState,
    clearGrouping,
    refreshTable,
    headerCell,
    columnCell,
    objectCountCell,
  };
};

export default useDynamicTable;
